/* Common Imports */

import { styled } from "@mui/system";
import { useRouter } from "next/router";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../models";
import StateInterface from "../../../redux-magic/state-interface";
import { deletePropertyDetailsThunk } from "../../../redux-magic/store";

/* Component Imports */

import {
	// IconButton,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material";
import { listingDeleteRefusalText } from "../../../lib/listings/listingDeleteRefusal";
import BeegruButton from "../../common-components/buttons/BeegruButton";

/* Icon Imports */

// import { Close } from "@mui/icons-material";

/* Styled Components */

const CustomDialog = styled(Dialog)(({ theme }) => ({
	padding: "1rem 1rem 1rem 1rem",
}));

const CustomDialogTitle = styled(DialogTitle)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	padding: "0.5rem 1rem 0.5rem 1rem",
	fontSize: "1.25rem",
	fontWeight: 400,
	background: theme.palette.background.paper,
}));

const CustomDialogContent = styled(DialogContent)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "1.5rem",
	padding: "2rem 1rem 1rem 1rem",
	background: theme.palette.background.paper,
	[theme.breakpoints.down("sm")]: {
		flexDirection: "column",
	},
}));

const CustomDialogActions = styled(DialogActions)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	width: "100%",
	gap: "1rem",
	padding: "0rem 1rem 1rem 0rem",
	background: theme.palette.background.paper,
}));

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const DeletePropertyModal = ({
	creator_user_id,
	creator_business_profile_id,
	propertyData,
	open,
	handleClose,
	dispatch,
	isEditor,
	profile_context,
	session,
}: {
	creator_user_id: string | ObjectId;
	creator_business_profile_id: string | ObjectId;
	propertyData: LocalPropertyInterface;
	open: boolean;
	handleClose: any;
	dispatch: Function;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	session: any;
}) => {
	const router = React.useRef(useRouter()).current;

	/* Translations */

	const localLang = {
		chats: router.locale === "ar-AE" ? "الدردشات" : router.locale === "kn-IN" ? "ಚಾಟ್‌ಗಳು" : "Chats",
		messages: router.locale === "ar-AE" ? "الرسائل" : router.locale === "kn-IN" ? "ಸಂದೇಶಗಳು" : "Messages",
		close: router.locale === "ar-AE" ? "إغلاق" : router.locale === "kn-IN" ? "ಮುಚ್ಚಿ" : "Close",
		cancel: router.locale === "ar-AE" ? "إلغاء" : router.locale === "kn-IN" ? "ರದ್ದು" : "Cancel",
		delete: router.locale === "ar-AE" ? "حذف" : router.locale === "kn-IN" ? "ಅಳಿಸಿ" : "Delete",
		are_you_sure:
			router.locale === "ar-AE"
				? "هل أنت متأكد أنك تريد حذف هذا العقار؟"
				: router.locale === "kn-IN"
					? "ನೀವು ಈ ಆಸ್ತಿಯನ್ನು ಅಳಿಸಲು ಖಚಿತವಾಗಿ ಇಚ್ಛಿಸುತ್ತೀರಾ?"
					: "Are you sure you want to delete this property?",
		info_text:
			router.locale === "ar-AE"
				? "سيتم حذف هذا العقار مع جميع البثوث والتعزيزات المرتبطة به نهائيًا، ولا يمكنك التراجع عن هذا الإجراء"
				: router.locale === "kn-IN"
					? "ಈ ಆಸ್ತಿ ಮತ್ತು ಅದರೊಂದಿಗೆ ಸಂಬಂಧಿತ ಪ್ರಸಾರಗಳು ಮತ್ತು ಬೂಸ್ಟುಗಳನ್ನು ಶಾಶ್ವತವಾಗಿ ಅಳಿಸಲಾಗುತ್ತದೆ, ಮತ್ತು ನೀವು ಈ ಕ್ರಿಯೆಯನ್ನು ಹಿಂತೆಗೆದುಕೊಳ್ಳಲಾಗದು"
					: "This property with all related broadcasts and boosts will be deleted permanently and you cannot undo this action",
	};

	const [submitting, setSubmitting] = React.useState<boolean>(false);
	// Why a delete didn't go through (a broadcast in review, or a failure); shown in place of the warning.
	const [refusal, setRefusal] = React.useState<string>("");
	React.useEffect(() => {
		if (open) setRefusal("");
	}, [open]);

	/*

		& Let's declare the router. Here we are using the useRouter hook from nextjs.

		& We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

	*/

	const creator_type: string = propertyData
		? propertyData.created_by
			? propertyData.created_by.is_business_profile
				? (propertyData.created_by.business_profile_id as BusinessProfileInterface).business_profile_type + "s"
				: "users"
			: "users"
		: "users";

	const handleDelete = async () => {
		setSubmitting(true);
		const result: { ok: boolean; code: string } = await dispatch(
			deletePropertyDetailsThunk({
				property_id: propertyData._id,
				is_business_profile: isEditor ? profile_context.is_business_profile : true,
				user_id: isEditor ? profile_context.user_id : creator_user_id,
				business_profile_id: isEditor ? profile_context.business_profile_id : creator_business_profile_id,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				url: router.asPath ? router.asPath : "",
				locale: router.locale ? router.locale : "en-IN",
				profile_context: profile_context,
			}),
		);
		if (!result?.ok) {
			setSubmitting(false);
			setRefusal(listingDeleteRefusalText(result?.code || "", router.locale));
			return;
		}

		window.open(
			`/${creator_type}/` +
				(propertyData.created_by.is_business_profile
					? (propertyData.created_by.business_profile_id as BusinessProfileInterface).slug
					: ((propertyData.created_by.user_id as UserInterface)._id as string)),
			"_self",
		);

		// router.push({
		//   pathname:
		//     `/${creator_type}/` +
		//     (propertyData.created_by.is_business_profile
		//       ? (
		//           propertyData.created_by
		//             .business_profile_id as BusinessProfileInterface
		//         ).slug
		//       : ((propertyData.created_by.user_id as UserInterface)._id as string)),
		//   query: {},
		// });
	};

	return (
		<React.Fragment>
			<CustomDialog
				open={open}
				onClose={handleClose}
				maxWidth="xs"
				fullWidth={true}
				disableScrollLock={true}
			>
				<CustomDialogTitle>{localLang.are_you_sure}</CustomDialogTitle>

				<CustomDialogContent>
					<Typography variant="body2">{refusal || localLang.info_text}</Typography>
				</CustomDialogContent>

				<CustomDialogActions>
					{!submitting ? (
						<React.Fragment>
							<BeegruButton
								flavor="primary"
								variant="outlined"
								onClick={handleClose}
								disabled={submitting}
								name="cancel"
								type="button"
							>
								{localLang.cancel}
							</BeegruButton>

							<BeegruButton
								flavor="primary"
								variant="contained"
								disabled={submitting}
								onClick={() => {
									handleDelete();
								}}
								name="submit"
								type="button"
							>
								{localLang.delete}
							</BeegruButton>
						</React.Fragment>
					) : (
						<CircularProgress />
					)}
				</CustomDialogActions>
			</CustomDialog>
		</React.Fragment>
	);
};

export default DeletePropertyModal;
